// サイト管理サービスの実装
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  query, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { LandingPage, Page } from '../types';
import { ImageService } from './imageService';
import { DomainService } from './domainService';

export class SiteService {
  private static readonly COLLECTION_NAME = 'landingPages';

  // サイト一覧取得
  static async getSites(): Promise<LandingPage[]> {
    try {
      
      
      // タイムアウト処理を追加
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Firebase接続がタイムアウトしました')), 10000);
      });

      const queryPromise = (async () => {
        // 全サイトを取得（ユーザー制限なし）
        const q = query(
          collection(db, this.COLLECTION_NAME),
          orderBy('updatedAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        
        const sites: LandingPage[] = [];
        
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // 論理削除済みは一覧に含めない
          if (data.deletedAt) return;

          sites.push({
            id: docSnap.id,
            title: data.title || '無題のサイト',
            description: data.description || '',
            pages: data.pages || [],
            status: data.status || 'draft',
            publishedUrl: data.publishedUrl,
            settings: data.settings,
            customDomain: data.customDomain,
            sslEnabled: data.sslEnabled,
            maxRows: data.maxRows,
            maxColumns: data.maxColumns,
            createdByUserId: data.createdByUserId || data.createdBy?.userId,
            updatedByUserId: data.updatedByUserId || data.updatedBy?.userId,
            companyUserId: data.companyUserId,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          });
        });
        
        return sites;
      })();

      return await Promise.race([queryPromise, timeoutPromise]);
    } catch (error) {
      console.error('サイト一覧の取得に失敗しました:', error);
      console.error('エラー詳細:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      // エラーが発生しても空の配列を返す
      return [];
    }
  }

  // サイト詳細取得
  static async getSite(siteId: string): Promise<LandingPage | null> {
    try {
      console.log('SiteService.getSite: サイトID:', siteId);
      const docRef = doc(db, this.COLLECTION_NAME, siteId);
      const docSnap = await getDoc(docRef);
      
      console.log('SiteService.getSite: ドキュメント存在:', docSnap.exists());
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        // 論理削除済みの場合は null を返す
        if (data.deletedAt) return null;
        console.warn('SiteService.getSite: ドキュメントデータ:', data);
        console.warn('SiteService.getSite: pagesデータ:', JSON.stringify(data.pages, null, 2));
        console.warn('SiteService.getSite: pagesの型:', typeof data.pages);
        console.warn('SiteService.getSite: pagesが配列か:', Array.isArray(data.pages));
        console.warn('SiteService.getSite: pagesの長さ:', data.pages?.length);
        if (data.pages && Array.isArray(data.pages) && data.pages.length > 0) {
          console.warn('SiteService.getSite: 最初のページ:', data.pages[0]);
        }
        
        // pagesが空配列または存在しない場合の警告
        if (!data.pages || !Array.isArray(data.pages) || data.pages.length === 0) {
          console.warn('SiteService.getSite: 警告 - pagesが空または不正な形式です:', {
            siteId,
            pages: data.pages,
            pagesType: typeof data.pages,
            isArray: Array.isArray(data.pages),
            pagesLength: data.pages?.length
          });
        }
        
        const result = {
          id: docSnap.id,
          title: data.title,
          description: data.description || '',
          pages: data.pages || [],
          status: data.status || 'draft',
          publishedUrl: data.publishedUrl,
          settings: data.settings,
          customDomain: data.customDomain,
          sslEnabled: data.sslEnabled,
          maxRows: data.maxRows,
          maxColumns: data.maxColumns,
          createdByUserId: data.createdByUserId || data.createdBy?.userId,
          updatedByUserId: data.updatedByUserId || data.updatedBy?.userId,
          companyUserId: data.companyUserId,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
        
        console.warn('SiteService.getSite: 返却データ:', result);
        console.warn('SiteService.getSite: 返却データのpages:', JSON.stringify(result.pages, null, 2));
        console.warn('SiteService.getSite: 返却データのpagesの長さ:', result.pages.length);
        if (result.pages && Array.isArray(result.pages) && result.pages.length > 0) {
          console.warn('SiteService.getSite: 返却データの最初のページ:', result.pages[0]);
        }
        
        return result;
      }
      
      console.log('SiteService.getSite: ドキュメントが存在しません');
      return null;
    } catch (error) {
      console.error('サイト詳細の取得に失敗しました:', error);
      throw error;
    }
  }

  /** Firestoreは undefined を許容しないため、オブジェクトから再帰的に undefined を除去 */
  private static removeUndefined(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        result[key] = value.map((item) =>
          item !== null && typeof item === 'object' && !Array.isArray(item) && !(item instanceof Date) && !(item instanceof Timestamp)
            ? this.removeUndefined(item as Record<string, unknown>)
            : item
        );
      } else if (value !== null && typeof value === 'object' && !(value instanceof Date) && !(value instanceof Timestamp)) {
        result[key] = this.removeUndefined(value as Record<string, unknown>);
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  // サイト作成
  static async createSite(siteData: Omit<LandingPage, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const now = Timestamp.now();
      const dataWithoutUndefined = this.removeUndefined({
        ...siteData,
        createdAt: now,
        updatedAt: now,
      } as Record<string, unknown>);
      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), dataWithoutUndefined);
      
      return docRef.id;
    } catch (error) {
      console.error('サイトの作成に失敗しました:', error);
      throw error;
    }
  }

  // サイト更新
  static async updateSite(siteId: string, siteData: Partial<LandingPage>): Promise<void> {
    try {
      if (!siteId || siteId === 'create') {
        throw new Error('無効なサイトIDです');
      }

      console.log('サイト更新開始:', { siteId, siteData });
      
      const docRef = doc(db, this.COLLECTION_NAME, siteId);
      
      // 既存のドキュメントを取得してuserIdを確認
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('サイトが見つかりません');
      }

      const existingData = docSnap.data();
      if (existingData.deletedAt) {
        throw new Error('削除済みのサイトは更新できません');
      }
      const updateData: any = {
        ...siteData,
        updatedAt: Timestamp.now(),
      };

      // settings は既存とマージする。undefined のキーは「削除」として扱いマージ結果から除外する
      if (updateData.settings != null && existingData.settings && typeof existingData.settings === 'object') {
        const merged: Record<string, unknown> = { ...existingData.settings };
        for (const [key, value] of Object.entries(updateData.settings)) {
          if (value === undefined || value === null) {
            delete merged[key];
          } else {
            merged[key] = value;
          }
        }
        updateData.settings = merged;
      }
      
      // titleが存在しない場合は追加
      if (!existingData.title && siteData.title) {
        updateData.title = siteData.title;
      }
      
      // id, createdAt, updatedAtは更新しない
      delete updateData.id;
      delete updateData.createdAt;
      delete updateData.updatedAt;
      
      // undefined値を除外（companyUserIdは明示的にnullを設定する場合は保存）
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });
      
      // companyUserIdがsiteDataに含まれている場合は保存（undefinedでも明示的に設定）
      if ('companyUserId' in siteData) {
        updateData.companyUserId = siteData.companyUserId ?? null;
      }
      
      console.log('更新データ:', updateData);
      
      await updateDoc(docRef, updateData);
      console.log('サイト更新完了');
    } catch (error) {
      console.error('サイトの更新に失敗しました:', error);
      throw error;
    }
  }

  // サイト削除（論理削除: deletedAt を設定）
  static async deleteSite(siteId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, siteId);
      await updateDoc(docRef, {
        deletedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('サイトの削除に失敗しました:', error);
      throw error;
    }
  }


  // 画像アップロード（ImageServiceを使用して統一）
  static async uploadImage(file: File, siteId: string, pageId?: string): Promise<string> {
    try {
      // ImageServiceを使用してWebP変換とライブラリパスに統一
      return await ImageService.uploadImage(file, siteId, pageId);
    } catch (error) {
      console.error('画像のアップロードに失敗しました:', error);
      throw error;
    }
  }

  // 画像削除
  static async deleteImage(imageUrl: string): Promise<void> {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (error) {
      console.error('画像の削除に失敗しました:', error);
      throw error;
    }
  }

  // サイト公開
  static async publishSite(siteId: string, customDomain?: string): Promise<string> {
    try {
      const site = await this.getSite(siteId);
      if (!site) {
        throw new Error('サイトが見つかりません');
      }

      // 公開URLを生成（Firebase Hostingの制限により、パス形式を使用）
      const publishedUrl = customDomain 
        ? `https://qwipesystem.web.app/sites/${customDomain}`
        : `https://qwipesystem.web.app/sites/${siteId}`;

      // サイトを公開状態に更新
      await this.updateSite(siteId, {
        status: 'published',
        publishedUrl,
      });

      return publishedUrl;
    } catch (error) {
      console.error('サイトの公開に失敗しました:', error);
      throw error;
    }
  }

  // サイト非公開
  static async unpublishSite(siteId: string): Promise<void> {
    try {
      await this.updateSite(siteId, {
        status: 'unpublished',
        publishedUrl: undefined,
      });
    } catch (error) {
      console.error('サイトの非公開に失敗しました:', error);
      throw error;
    }
  }

  /**
   * サイトの実際のステータスを計算
   * - ドメインが設定されていない → 下書き
   * - ドメインが設定されている + 公開設定が非公開 → 非公開
   * - ドメインが設定されている + 公開設定が公開 → 公開
   * @param site サイトデータ
   * @returns 実際のステータス
   */
  static async getActualStatus(site: LandingPage): Promise<'published' | 'unpublished' | 'draft'> {
    try {
      // ドメイン設定を取得
      const domainConfig = await DomainService.getDomain(site.id);
      
      // ドメインが設定されていない場合は下書きとして扱う
      if (!domainConfig || !domainConfig.isActive) {
        return 'draft';
      }
      
      // ドメインが設定されている場合、公開設定に基づいて判定
      if (site.status === 'published') {
        return 'published';
      } else {
        return 'unpublished';
      }
    } catch (error) {
      console.error('ステータス計算エラー:', error);
      // エラー時は安全側に倒して下書きとする
      return 'draft';
    }
  }

  /**
   * サイトの実際のステータスを計算（同期版、ドメイン情報を既に取得済みの場合）
   * @param site サイトデータ
   * @param domainConfig ドメイン設定（nullの場合は未設定）
   * @returns 実際のステータス
   */
  static getActualStatusSync(site: LandingPage, domainConfig: { isActive: boolean } | null): 'published' | 'unpublished' | 'draft' {
    // ドメインが設定されていない場合は下書きとして扱う
    if (!domainConfig || !domainConfig.isActive) {
      return 'draft';
    }
    
    // ドメインが設定されている場合、公開設定に基づいて判定
    if (site.status === 'published') {
      return 'published';
    } else {
      return 'unpublished';
    }
  }
}
